import React from 'react';
import type { ElementRef, ReactNode } from 'react';

import { styled, theme } from '@backyard-ui/styles';
import type { StitchesCSS, CSSProperties } from '@backyard-ui/styles';

import * as styles from './Stack.styles';

import { filterFalsyChildren, filterFalsyValue } from '../../utils';

const StackEl = styled('div', styles.gridStyles);

export interface StackProps {
  /**
   * The space between its children
   */
  spacing?: keyof typeof theme.space;

  /**
   * The CSS `align-items` property
   */
  align?: CSSProperties['alignItems'];

  /**
   * The CSS `flex-direction` property
   */
  direction?: CSSProperties['flexDirection'];

  /**
   * The CSS `justify-content` property
   */
  justify?: CSSProperties['justifyContent'];

  /**
   * If true the component will occupy remaining space
   * by setting 'flex-grow: 1'
   */
  isGrow?: boolean;

  /**
   * The Stitches `css` func
   */
  css?: StitchesCSS;

  /**
   * The stack children
   */
  children: ReactNode;
}

const Stack = React.forwardRef<ElementRef<typeof StackEl>, StackProps>(
  (props, ref) => {
    const { children, spacing = '4', align, justify, ...rest } = props;

    const validChildren = filterFalsyChildren(children);
    const styles = filterFalsyValue({
      alignItems: align,
      justifyContent: justify,
      $$spacing: theme.space[spacing],
    });

    return (
      <StackEl
        css={{
          ...styles,
        }}
        ref={ref}
        {...rest}
      >
        {validChildren.map((child, index) => (
          <div key={index}>{child}</div>
        ))}
      </StackEl>
    );
  }
);

Stack.displayName = 'Stack';

export default Stack;
